import http from 'src/http/HttpCommon';
import CONTENT_TYPES from './contentTypes';

class PartnerService {
  getPartners = async ({
    page,
    size,
    orderBy,
    order,
    status,
    searchTerm
  }) => {
    try {
      const response = await http.get('/api/admin/partners', {
        headers: {
          'Content-Type': CONTENT_TYPES.partners
        },
        data: {},
        params: {
          page,
          size,
          orderBy,
          order,
          status,
          searchTerm
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getPartnerById = async (id) => {
    try {
      const response = await http.get(`/api/admin/partners/${id}`, {
        headers: {
          'Content-Type': CONTENT_TYPES.partners
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  addPartner = async ({
    name,
    code,
    profileImage,
    countryCode,
    currencyCode,
    status
  }) => {
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('code', code);
      formData.append('image', profileImage);
      formData.append('countryCode', countryCode);
      formData.append('currencyCode', currencyCode);
      formData.append('status', status);

      const response = await http.post('/api/admin/partners', formData, {
        headers: {
          'Content-Type': CONTENT_TYPES.multipart_formdata
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  updatePartner = async ({
    id,
    name,
    code,
    profileImage,
    countryCode,
    currencyCode,
    status
  }) => {
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('code', code);
      formData.append('image', profileImage);
      formData.append('countryCode', countryCode);
      formData.append('currencyCode', currencyCode);
      formData.append('status', status);

      const response = await http.put(`/api/admin/partners/${id}`, formData, {
        headers: {
          'Content-Type': CONTENT_TYPES.multipart_formdata
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  }

  deletePartner = async (id) => {
    try {
      return await http.delete(`/api/admin/partners/${id}`, {
        headers: {
          'Content-Type': CONTENT_TYPES.partners
        },
        data: {}
      });
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getPartnerContactById = async (id) => {
    try {
      const response = await http.get(`/api/admin/partners/${id}/contact`, {
        headers: {
          'Content-Type': CONTENT_TYPES.partners_contact
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  updateContactByPartnerId = async ({
    id,
    website,
    email,
    dialCode,
    phone,
    address
  }) => {
    try {
      const response = await http.put(`/api/admin/partners/${id}/contact`,
        {
          website,
          email,
          dialCode,
          phone,
          address
        }, {
        headers: {
          'Content-Type': CONTENT_TYPES.partners_contact
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getPartnerSettingsById = async (id) => {
    try {
      const response = await http.get(`/api/admin/partners/${id}/settings`, {
        headers: {
          'Content-Type': CONTENT_TYPES.partners_settings
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  updateSettingsByPartnerId = async ({
    id,
    webhookUrl,
    successCallbackUrl,
    failureCallbackUrl,
    pendingCallbackUrl,
    cancelCallbackUrl
  }) => {
    try {
      const response = await http.put(`/api/admin/partners/${id}/settings`,
        {
          webhookUrl,
          successCallbackUrl,
          failureCallbackUrl,
          pendingCallbackUrl,
          cancelCallbackUrl
        }, {
        headers: {
          'Content-Type': CONTENT_TYPES.partners_settings
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  getCredentialsByPartnerId = async (id) => {
    try {
      const response = await http.get(`/api/admin/partners/${id}/credentials`, {
        headers: {
          'Content-Type': CONTENT_TYPES.partners_credentials
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  }

  addCredentialsByPartnerId = async ({ id, type }) => {
    try {
      const response = await http.post(`/api/admin/partners/${id}/credentials`, { type }, {
        headers: {
          'Content-Type': CONTENT_TYPES.partners_credentials
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  }

  getChannelsByPartnerId = async (partnerId) => {
    try {
      const response = await http.get('/api/admin/payments/channels/partners', {
        headers: {
          'Content-Type': CONTENT_TYPES.partners_channels
        },
        data: {},
        params: {
          partnerId
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  };

  saveChannelsByPartnerId = async ({ partnerId, paymentChannelIds }) => {
    try {
      const response = await http.post('api/admin/payments/channels/partners', {
        partnerId,
        paymentChannelIds
      }, {
        headers: {
          'Content-Type': CONTENT_TYPES.partners_channels
        },
      });

      return response;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  }

  getPermittedPartners = async () => {
    try {
      const response = await http.get('/api/admin/partners/permitted', {
        headers: {
          'Content-Type': CONTENT_TYPES.partners
        },
        data: {}
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  }

  sendPartnerPaymentWebhook = async (reference) => {
    try {
      const response = await http.get('/api/admin/payments/partner/webhook', {
        headers: {
          'Content-Type': CONTENT_TYPES.partners_webhooks
        },
        data: {},
        params: {
          reference
        }
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  }
}

const partnerService = new PartnerService();
export default partnerService;
